import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { useWallet } from '@/hooks/useWallet';
import { Wallet, ArrowRight, Loader2, ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface WalletConnectButtonProps {
  variant?: 'hero' | 'default' | 'outline';
  size?: 'default' | 'sm' | 'lg' | 'xl';
  showArrow?: boolean;
}

export const WalletConnectButton = ({ 
  variant = 'hero', 
  size = 'xl',
  showArrow = true 
}: WalletConnectButtonProps) => {
  const { address, isConnected, isConnecting, connectWithConnector, connectors, disconnect } = useWallet();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  const formatAddress = (addr: string) => {
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  if (isConnected && address) {
    return (
      <div className="flex items-center gap-3">
        <Button 
          variant="hero" 
          size={size === 'xl' ? 'lg' : size}
          onClick={() => navigate('/dashboard')}
          className="group"
        >
          Go to Dashboard
          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
        </Button>
        <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size={size === 'xl' ? 'lg' : size} className="font-mono">
              {formatAddress(address)}
              <ChevronDown className="w-4 h-4 ml-2" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-card border-border">
            <DropdownMenuItem 
              onClick={() => {
                disconnect();
                setIsOpen(false);
              }}
              className="text-destructive cursor-pointer"
            >
              Disconnect
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  }

  if (isConnecting) {
    return (
      <Button variant={variant} size={size} disabled>
        <Loader2 className="w-5 h-5 animate-spin" />
        Connecting...
      </Button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant={variant} size={size} className="group">
          <Wallet className="w-5 h-5" />
          Connect Wallet
          {showArrow && <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="center" className="bg-card border-border w-56">
        {connectors.map((connector) => (
          <DropdownMenuItem
            key={connector.id}
            onClick={() => connectWithConnector(connector.id)}
            className="cursor-pointer"
          >
            {connector.name}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
